import {
    AppConfig,
    getConfig,
    PushNotificationType,
} from '@superbet-group/web.lib.config';
import { instanceGuard } from '@superbet-group/web.lib.utilities';
import {
    Observable,
    Subscription,
} from 'rxjs';
import {
    share,
    filter,
} from 'rxjs/operators';

import SingletonError from '@core/services/common/errors/SingletonError';

import SignalRSocket from './_private/SignalRSocket';
import Singleton from '../common/Singleton';
import { createEventSourceObservable } from '@core/services/pushNotifications/helpers';
import {
    PushEvent,
    PushEventType,
    ServerMessageMapper,
} from '@core/services/pushNotifications/types';
import { transformBalance } from '@core/mappers/user/pushNotifications/transformBalance';
import { transformPayment } from '@core/mappers/user/pushNotifications/transformPayment';
import { transformMessage } from '@core/mappers/user/pushNotifications/transformMessage';
import IMessageDto from '@core/mappers/user/dtos/IMessageDto';
import { MessageDto } from '@core/mappers/user/pushNotifications/dtos/MessageDto';
import { BalanceDto } from '@core/mappers/user/pushNotifications/dtos/BalanceDto';
import { PaymentDto } from '@core/mappers/user/pushNotifications/dtos/PaymentDto';

const eventTypeMappers: ServerMessageMapper = {
    [PushEventType.Balance]: transformBalance,
    [PushEventType.Payment]: transformPayment,
    [PushEventType.Message]: transformMessage,
};

interface NotificationsSocket {
    connect(
        playerId: string,
        sessionId: string,
        callback: (error: Error | null, message: IMessageDto | null) => void
    ): void;
    disconnect: () => void;
}

class PushNotificationsService {
    private static instance?: PushNotificationsService;
    private notificationsSocket: NotificationsSocket;
    private connected = false;
    private events$: Observable<PushEvent> = new Observable();
    private subscriptions: Subscription = new Subscription();
    private pushNotificationUrl: string;
    private pushNotificationType: PushNotificationType;
    private playerId: string;
    private sessionId: string;

    constructor(config: AppConfig, socketService: NotificationsSocket) {
        if (PushNotificationsService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.pushNotificationUrl = config.user.pushNotificationsUrl!;
        this.pushNotificationType = config.countrySpecific.pushNotificationType;
        this.notificationsSocket = socketService;
    }

    public connect(playerId: string, sessionId: string) {
        this.playerId = playerId;
        this.sessionId = sessionId;
        this._connect();
    }

    private _connect() {
        if (this.pushNotificationType === PushNotificationType.ServerSentEvent && !this.connected) {
            this.connectToServerSentEvents();
            this.connected = true;
        } else if (this.pushNotificationType === PushNotificationType.NotificationSocketService) {
            this.connectToNotificationSocket(this.playerId, this.sessionId);
            this.connected = true;
        }
    }

    private connectToServerSentEvents() {
        this.events$ = createEventSourceObservable<PushEvent>(`${this.pushNotificationUrl}?lang=${getConfig().appLocale.locale}`, true, [PushEventType.Payment, PushEventType.BonusUpdate]);
    }

    private connectToNotificationSocket(playerId: string, sessionId: string) {
        this.events$ = new Observable<PushEvent>((subscriber) => {
            this.notificationsSocket.connect(playerId, sessionId, (error, message) => {
                if (error) {
                    this.disconnect();
                } else if (message) {
                    subscriber.next({ type: PushEventType.Message, data: { Message: message, StatusCode: 'Success' } });
                }
            });
        }).pipe(share());
    }

    public subscribe(eventType: PushEventType, callback: (data: any) => void) {
        if (!this.connected) {
            this._connect();
        }
        const subscription = this.events$.pipe(
            filter((event) => event.type === eventType),
        ).subscribe((event) => {
            if (eventType === PushEventType.BonusUpdate) {
                callback(event.data);
            } else if (eventTypeMappers[eventType]) {
                switch (eventType) {
                    case PushEventType.Message:
                        callback(eventTypeMappers[eventType](event.data as MessageDto));
                        break;
                    case PushEventType.Balance:
                        callback(eventTypeMappers[eventType](event.data as BalanceDto));
                        break;
                    case PushEventType.Payment:
                        callback(eventTypeMappers[eventType](event.data as PaymentDto));
                        break;
                    default:
                        break;
                }
            }
        });
        this.subscriptions.add(subscription);
        return subscription;
    }

    public disconnect() {
        this.subscriptions.unsubscribe();
        this.connected = false;
        this.subscriptions = new Subscription();
    }

    public static getInstance(): PushNotificationsService | undefined {
        return instanceGuard(PushNotificationsService.instance);
    }

    public static createInstance(...args: ConstructorParameters<typeof PushNotificationsService>) {
        if (!PushNotificationsService.instance) {
            PushNotificationsService.instance = new PushNotificationsService(...args);
        }
        return PushNotificationsService.instance;
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete PushNotificationsService.instance;
    }
}
export { SignalRSocket };
export default PushNotificationsService as Singleton<PushNotificationsService>;
